import {
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
  type Unsubscribe,
} from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import type {
  LocationAlertCreateDTO,
  LocationAlertDescriptionWriteDTO,
  LocationAlertDTO,
  LocationAlertPathRef,
  LocationAlertUpdateDTO,
} from '@src/models/interfaces/locationAlert.interface';
import getDocData from '@src/packages/utils/firebase/getDocData';

export class LocationAlertRepository {
  getCollectionPath(orgId: string, divId: string): string {
    return `${FSCollections.ORGANIZATIONS}/${orgId}/${FSCollections.DIVISIONS}/${divId}/${FSCollections.LOCATION_ALERTS}`;
  }

  private getCollectionRef(orgId: string, divId: string) {
    return collection(
      db,
      FSCollections.ORGANIZATIONS,
      orgId,
      FSCollections.DIVISIONS,
      divId,
      FSCollections.LOCATION_ALERTS,
    );
  }

  getDocRef(orgId: string, divId: string, locationAlertId: string) {
    return doc(this.getCollectionRef(orgId, divId), locationAlertId);
  }

  private toCreateDocument(data: LocationAlertCreateDTO) {
    return {
      ...data,
      descriptions: data.descriptions ?? [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
  }

  /**
   * Fan-out subscribe across division collections, then merge snapshots.
   */
  subscribeByDivisions(
    orgId: string,
    divIds: string[],
    onData: (alerts: LocationAlertDTO[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    if (!orgId || divIds.length === 0) {
      onData([]);
      return () => undefined;
    }

    const cache = new Map<string, LocationAlertDTO[]>();
    const emit = () => {
      onData(Array.from(cache.values()).flat());
    };

    const unsubscribers = divIds.map(divId =>
      onSnapshot(
        query(this.getCollectionRef(orgId, divId)),
        snapshot => {
          const alerts = snapshot.docs
            .map(document => getDocData(document) as LocationAlertDTO | null)
            .filter((alert): alert is LocationAlertDTO => alert != null);
          cache.set(divId, alerts);
          emit();
        },
        error => {
          onError?.(error);
        },
      ),
    );

    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }

  subscribeOne(
    orgId: string,
    divId: string,
    locationId: string,
    onData: (alerts: LocationAlertDTO[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    if (!orgId || !divId || !locationId) {
      onData([]);
      return () => undefined;
    }

    const alertsQuery = query(
      this.getCollectionRef(orgId, divId),
      where('id', '==', locationId),
    );

    return onSnapshot(
      alertsQuery,
      snapshot => {
        const alerts = snapshot.docs
          .map(document => getDocData(document) as LocationAlertDTO | null)
          .filter((alert): alert is LocationAlertDTO => alert != null);
        onData(alerts);
      },
      error => {
        onError?.(error);
      },
    );
  }

  async update(
    orgId: string,
    divId: string,
    locationAlertId: string,
    data: LocationAlertUpdateDTO,
  ): Promise<void> {
    await updateDoc(this.getDocRef(orgId, divId, locationAlertId), {
      ...data,
      updatedAt: serverTimestamp(),
    });
  }

  async incrementQuantity(orgId: string, divId: string, locationAlertId: string): Promise<void> {
    await updateDoc(this.getDocRef(orgId, divId, locationAlertId), {
      quantity: increment(1),
      updatedAt: serverTimestamp(),
    });
  }

  async exists(orgId: string, divId: string, locationAlertId: string): Promise<boolean> {
    const snapshot = await getDoc(this.getDocRef(orgId, divId, locationAlertId));
    return snapshot.exists();
  }

  async listLocationKeys(orgId: string, divIds: string[]): Promise<LocationAlertPathRef[]> {
    if (!orgId || divIds.length === 0) {
      return [];
    }

    const snapshots = await Promise.all(
      divIds.map(divId => getDocs(this.getCollectionRef(orgId, divId))),
    );

    return snapshots.flatMap((snapshot, index) => {
      const divId = divIds[index];
      return snapshot.docs.map(document => ({
        divId,
        locationId: document.id,
      }));
    });
  }

  async createWithQuantity(
    orgId: string,
    divId: string,
    locationAlertId: string,
    data: LocationAlertCreateDTO,
  ): Promise<void> {
    await setDoc(this.getDocRef(orgId, divId, locationAlertId), this.toCreateDocument(data));
  }

  async reassign(
    orgId: string,
    from: LocationAlertPathRef,
    to: LocationAlertPathRef,
    data: LocationAlertCreateDTO,
  ): Promise<void> {
    const batch = writeBatch(db);
    batch.set(this.getDocRef(orgId, to.divId, to.locationId), this.toCreateDocument(data));
    batch.delete(this.getDocRef(orgId, from.divId, from.locationId));
    await batch.commit();
  }

  async delete(orgId: string, divId: string, locationAlertId: string): Promise<void> {
    await deleteDoc(this.getDocRef(orgId, divId, locationAlertId));
  }

  async appendDescription(
    orgId: string,
    divId: string,
    locationAlertId: string,
    data: LocationAlertDescriptionWriteDTO,
  ): Promise<void> {
    await updateDoc(this.getDocRef(orgId, divId, locationAlertId), {
      descriptions: arrayUnion({
        description: data.description,
        date: Timestamp.now(),
      }),
      updatedAt: serverTimestamp(),
    });
  }
}
